import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { CheckBadgeIcon } from '../../components/icons/Icons';

// --- CONSTANTS ---
const BRAND_COLORS = {
    darkBlue: "#213368",
    teal: "#20BEC6",
    orange: "#F4A866",
    red: "#D95448"
};

const PILLAR_TARGETS = {
    technical: { min: 10, max: 15, label: "Technical Skills", color: BRAND_COLORS.teal, bg: "bg-teal-50", border: "border-teal-200" },
    soft: { min: 5, max: 10, label: "Soft Skills", color: BRAND_COLORS.orange, bg: "bg-orange-50", border: "border-orange-200" },
    peer: { min: 10, max: 10, label: "Peer Learning", color: BRAND_COLORS.red, bg: "bg-red-50", border: "border-red-200" },
    knowledge: { min: 5, max: 10, label: "Knowledge Enh.", color: BRAND_COLORS.darkBlue, bg: "bg-blue-50", border: "border-blue-200" }
};

// --- HELPERS ---
const getFinancialYear = (dateObj) => {
    if (!dateObj) return '';
    const date = new Date(dateObj);
    const year = date.getFullYear();
    const month = date.getMonth(); 
    if (month < 3) return `${year - 1}-${year}`;
    return `${year}-${year + 1}`;
};

export default function PDDashboard({ userProfile, appId }) {
    const [stats, setStats] = useState({ technical: 0, soft: 0, peer: 0, knowledge: 0 });
    const [allActivities, setAllActivities] = useState([]);
    const [filteredActivities, setFilteredActivities] = useState([]);
    const [yearOptions, setYearOptions] = useState([]);
    const [selectedYear, setSelectedYear] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            if (!userProfile?.id) return;
            try {
                const q = query(
                    collection(db, `artifacts/${appId}/pd_activities`), 
                    where('userId', '==', userProfile.id),
                    orderBy('createdAt', 'desc')
                );
                const snapshot = await getDocs(q);
                const fetchedDocs = snapshot.docs.map(doc => {
                    const data = doc.data();
                    const activityDate = data.date ? data.date.toDate() : data.createdAt.toDate();
                    return { id: doc.id, ...data, activityDate, fy: getFinancialYear(activityDate) };
                });
                setAllActivities(fetchedDocs);

                const currentFY = getFinancialYear(new Date());
                const uniqueYears = new Set([currentFY]); 
                fetchedDocs.forEach(doc => { if (doc.fy > currentFY) uniqueYears.add(doc.fy); });
                setYearOptions(Array.from(uniqueYears).sort().reverse());
                setSelectedYear(currentFY);
            } catch (error) { console.error("Error loading PD data:", error); }
        };
        fetchData();
    }, [appId, userProfile?.id]);

    useEffect(() => {
        if (!selectedYear) return;
        const currentYearData = allActivities.filter(item => item.fy === selectedYear);
        let newStats = { technical: 0, soft: 0, peer: 0, knowledge: 0 };
        currentYearData.forEach(item => {
            if (item.status === 'Completed') {
                const typeKey = item.type === 'Soft Skills' ? 'soft' : 
                                item.type === 'Knowledge' ? 'knowledge' : 
                                item.type === 'Peer Learning' ? 'peer' : 'technical';
                newStats[typeKey] += (Number(item.hours) || 0);
            }
        });
        setStats(newStats);
        setFilteredActivities(currentYearData);
    }, [selectedYear, allActivities]);

    const totalHours = Object.values(stats).reduce((a, b) => a + b, 0);
    const totalTarget = 40;

    // --- CIRCLE CALCULATION ---
    const radius = 80;
    const circumference = 2 * Math.PI * radius; // ~502.65
    
    // Calculate stroke length for each segment based on hours
    // We cap the total at 100% for the visual circle to prevent overlap weirdness if they exceed target
    const getStrokeLength = (hours) => (Math.min(hours, totalTarget) / totalTarget) * circumference;
    
    const techStroke = getStrokeLength(stats.technical);
    const softStroke = getStrokeLength(stats.soft);
    const peerStroke = getStrokeLength(stats.peer);
    const knowStroke = getStrokeLength(stats.knowledge);

    // Offsets determine where each segment starts (stacking them)
    // -90deg rotation is handled in SVG CSS, so we just offset by previous lengths
    const techOffset = 0; 
    const softOffset = -techStroke; 
    const peerOffset = -(techStroke + softStroke);
    const knowOffset = -(techStroke + softStroke + peerStroke);

    return (
        <div className="space-y-8">
            {/* HEADER */}
            <div className="flex flex-col sm:flex-row justify-between items-end sm:items-center border-b border-gray-100 pb-4">
                <div>
                    <h3 className="text-lg font-bold text-[#213368]">Your Annual Scorecard</h3>
                    <p className="text-sm text-gray-500">Financial Year Progress</p>
                </div>
                {yearOptions.length > 0 && (
                    <div className="mt-2 sm:mt-0">
                        <select 
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(e.target.value)}
                            className="block w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#20BEC6] focus:border-[#20BEC6] sm:text-sm rounded-md shadow-sm"
                        >
                            {yearOptions.map(yr => <option key={yr} value={yr}>FY {yr}</option>)}
                        </select>
                    </div>
                )}
            </div>

            {/* TOP STATS ROW (Colored Boxes) */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {Object.entries(PILLAR_TARGETS).map(([key, config]) => (
                    <PillarCard 
                        key={key} 
                        label={config.label} 
                        current={stats[key]} 
                        target={config.max} 
                        minTarget={config.min}
                        color={config.color}
                        bgClass={config.bg}
                        borderClass={config.border}
                    />
                ))}
            </div>

            {/* MAIN PROGRESS CIRCLE */}
            <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col md:flex-row items-center justify-center md:justify-around text-center md:text-left">
                <div className="mb-6 md:mb-0">
                    <h3 className="text-lg font-bold text-[#213368]">Total Hours Logged</h3>
                    <p className="text-sm text-gray-500 mb-4">Financial Year {selectedYear}</p>
                    <p className="mt-2 text-sm font-medium text-gray-600">
                        {totalHours >= totalTarget ? 
                            <span className="text-green-600 flex items-center justify-center md:justify-start">
                                <CheckBadgeIcon className="h-5 w-5 mr-1"/> Goal Reached!
                            </span> : 
                            <span className="text-orange-500">
                                {Math.max(0, totalTarget - totalHours)} hours remaining to reach annual target.
                            </span>
                        }
                    </p>
                </div>

                <div className="relative h-48 w-48 flex items-center justify-center">
                    {/* SVG Container */}
                    <svg className="h-full w-full">
                        {/* Grey Track */}
                        <circle cx="96" cy="96" r={radius} stroke="#f3f4f6" strokeWidth="16" fill="none" />
                        
                        {/* Segment 1: Technical (Teal) */}
                        <circle cx="96" cy="96" r={radius} stroke={PILLAR_TARGETS.technical.color} strokeWidth="16" fill="none"
                            strokeDasharray={`${techStroke} ${circumference}`} strokeDashoffset={techOffset} strokeLinecap="round" className="transform -rotate-90 origin-center transition-all duration-1000 ease-out" />

                        {/* Segment 2: Soft Skills (Orange) */}
                        <circle cx="96" cy="96" r={radius} stroke={PILLAR_TARGETS.soft.color} strokeWidth="16" fill="none"
                            strokeDasharray={`${softStroke} ${circumference}`} strokeDashoffset={softOffset} strokeLinecap="round" className="transform -rotate-90 origin-center transition-all duration-1000 ease-out" />

                        {/* Segment 3: Peer Learning (Red) */}
                        <circle cx="96" cy="96" r={radius} stroke={PILLAR_TARGETS.peer.color} strokeWidth="16" fill="none"
                            strokeDasharray={`${peerStroke} ${circumference}`} strokeDashoffset={peerOffset} strokeLinecap="round" className="transform -rotate-90 origin-center transition-all duration-1000 ease-out" />
                        
                        {/* Segment 4: Knowledge (Dark Blue) */}
                        <circle cx="96" cy="96" r={radius} stroke={PILLAR_TARGETS.knowledge.color} strokeWidth="16" fill="none"
                            strokeDasharray={`${knowStroke} ${circumference}`} strokeDashoffset={knowOffset} strokeLinecap="round" className="transform -rotate-90 origin-center transition-all duration-1000 ease-out" />
                    </svg>

                    {/* Text Overlay (No rotation here, so it stays horizontal) */}
                    <div className="absolute flex flex-col items-center justify-center inset-0">
                        <span className="text-4xl font-extrabold text-[#213368]">{totalHours}</span>
                        <span className="text-xs text-gray-400 font-medium uppercase mt-1">Goal: {totalTarget} Hrs</span>
                    </div>
                </div>
            </div>

            {/* ACTIVITY LIST */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                    <h3 className="font-bold text-[#213368]">Completed Trainings ({selectedYear})</h3>
                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded border">
                        {filteredActivities.length} Records
                    </span>
                </div>
                
                {filteredActivities.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">
                        No trainings found for this financial year.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-500 font-medium uppercase text-xs tracking-wider">
                                <tr>
                                    <th className="px-6 py-3">Activity / Course</th>
                                    <th className="px-6 py-3">Category</th>
                                    <th className="px-6 py-3">Date</th>
                                    <th className="px-6 py-3">Status</th>
                                    <th className="px-6 py-3 text-right">Hours</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {filteredActivities.map((activity) => (
                                    <tr key={activity.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 font-medium text-gray-800">
                                            {activity.title}
                                            {activity.linkedEventId && <span className="ml-2 text-[10px] text-[#20BEC6] bg-[#20BEC6]/10 px-1 rounded">Event</span>}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                                ${activity.type === 'Technical' ? 'bg-teal-50 text-teal-700' : 
                                                  activity.type === 'Soft Skills' ? 'bg-orange-50 text-orange-700' :
                                                  activity.type === 'Peer Learning' ? 'bg-red-50 text-red-700' : 
                                                  'bg-blue-50 text-blue-700'}`}>
                                                {activity.type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-500">
                                            {activity.activityDate.toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4">
                                            {activity.status === 'Completed' ? (
                                                <span className="text-green-600 flex items-center text-xs font-bold"><CheckBadgeIcon className="h-3 w-3 mr-1" /> Completed</span>
                                            ) : (
                                                <span className="text-gray-500 text-xs">{activity.status}</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-right font-bold text-[#213368]">
                                            {activity.hours}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

// --- SUB-COMPONENT: PILLAR CARD (Updated for color themes) ---
function PillarCard({ label, current, target, minTarget, color, bgClass, borderClass }) {
    const percentage = Math.min((current / target) * 100, 100);
    
    return (
        <div className={`${bgClass} border ${borderClass} p-4 rounded-xl shadow-sm flex flex-col justify-between h-32 relative overflow-hidden group hover:shadow-md transition`}>
            {/* Bottom Progress Bar */}
            <div className="absolute bottom-0 left-0 h-1.5 bg-white/50 w-full">
                <div 
                    className="h-full transition-all duration-1000 ease-out" 
                    style={{ width: `${percentage}%`, backgroundColor: color }}
                ></div>
            </div>
            
            <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{label}</p>
                <div className="flex items-baseline space-x-1">
                    <span className="text-3xl font-extrabold text-gray-800">{current}</span>
                    <span className="text-xs text-gray-500 font-medium">/ {minTarget}-{target} hrs</span>
                </div>
            </div>

            <div className="flex justify-between items-end">
                <span className="text-xs text-gray-500 font-medium">
                    {current >= minTarget ? 'Target Met' : `${minTarget - current} to min`}
                </span>
                {current >= minTarget && (
                    <div className="text-green-600 bg-white rounded-full p-1 shadow-sm">
                        <CheckBadgeIcon className="w-4 h-4" />
                    </div>
                )}
            </div>
        </div>
    );
}